import { stringify } from 'qs';

import { type Board, type Clip } from '@air/api/types';

import { sanitizeStringForUrl } from '~/utils/Url';

export const createBoardRoute = (id: string, title?: string): string =>
  `/b/${title ? `${sanitizeStringForUrl(title)}-` : ''}${id}`;

// @TODO(REV-821): Replace with actual collection route when implemented
export const createCollectionRoute = (): string => `/`;

export const createLibraryRoute = (id: string): string => `/l/${id}`;

export const createBoardClipRoute = ({
  clipId,
  boardTitle,
  boardId,
}: {
  clipId: Clip['id'];
  boardTitle: string;
  boardId: Board['id'];
}) => `${createBoardRoute(boardId, boardTitle)}/c/${clipId}`;

export const createSavedFilterRoute = (id: string) => `/saved-filter/${id}`;
export const createSavedSearchRoute = (id: string) => `/saved-search/${id}`;

export const createShortUrlBoardRoute = (shortId: string, subBoardId: string) => `/a/${shortId}/b/${subBoardId}`;

export const createShortUrlCollectionRoute = (shortId: string) => `/a/${shortId}`;

export const createShortUrlClipInBoardRoute = (shortId: string, subBoardId: string, clipId: Clip['id']) =>
  `/a/${shortId}/b/${subBoardId}/c/${clipId}`;

export const isSubscriptionCheckoutRoute = (pathname: string) => pathname.startsWith(Routes.workspaceAdmin.checkout);

export const Routes = {
  root: '/',
  apiDocs: 'https://developer.air.inc',
  apiKeyHelp: 'https://help.air.inc/en/articles/7047153-api-key-access#h_87ab5c7911',
  brandfolderMigration: 'https://www.notion.so/airinc/Brandfolder-Air-bbda48cadae545d0977e7012758e095a',
  enterprise: {
    expansion: 'https://forms.default.com/15399',
  },
  home: '/home',
  helpCenter: 'https://help.air.inc',
  facialRecognition: 'https://help.air.inc/en/articles/11062794-explore-facial-recognition',
  changelog: ' https://airlabs.canny.io/changelog',
  createWorkspace: '/workspace/create',
  joinApprovedWorkspace: '/workspace/join',
  joinWorkspace: '/workspace/join/[shortId]',
  joinWorkspaceWithToken: '/workspace/join/[shortId]',
  redirectWorkspace: '/workspace/redirect',
  workspaceMembersLimit: '/workspace/users-limit',
  noWorkspacePermission: '/no-workspace-permission',
  verify: '/verify/[dummyCode]/[token]',
  notFound: '/404',
  import: '/import/',
  canvases: '/canvases',
  creativePerformance: '/creative-performance',
  shareLinks: '/share-links',
  skills: '/skills',
  imports: {
    googleDrive: '/import/drive',
    homeBox: '/import/box',
    brandfolder: '/import/brandfolder',
    dropbox: '/import/dropbox',
    sharePoint: '/import/sharepoint',
  },
  generalLibrary: createLibraryRoute('general'),
  desktop: {
    openDesktopFile: '/open-desktop-file',
  },
  marketing: {
    root: 'https://air.inc',
    adobePremiereProDownload: 'https://partners.adobe.com/exchangeprogram/creativecloud/exchange.details.107604.html',
    desktop: 'https://air.inc/air-flow',
    pricing: 'https://air.inc/pricing',
    privacy: 'https://air.inc/privacy',
    terms: 'https://air.inc/terms',
  },
  auth: {
    login: '/login',
    logout: '/logout',
    forgot: '/forgot',
    samlSSOLogin: '/saml-sso-login',
    verifyEmailRequired: '/verify-email-required',
    /** Requires "email" query string */
    resetPassword: '/reset',
    oauth: '/oauth',
    oauthConsent: '/oauth/consent',
    signUp: '/sign-up',
  },
  popupConfirmation: '/popup-confirmation',
  onboarding: {
    createWorkspace: '/onboarding/workspace-name',
    approvedDomains: '/onboarding/join-approved-workspaces',
  },
  account: {
    apiAccess: '/account/api',
    myAccount: '/account/my-account',
    changePassword: '/account/change-password',
    confirmEmail: '/account/confirm-email',
    notificationSettings: '/account/notifications',
  },
  media: {
    favoriteItems: '/favorite-items',
    all: '/all',
    myUploads: '/my-uploads',
    recentlyAdded: '/recently-added',
    recentlyDeleted: '/recently-deleted',
    recentlyModified: '/recently-modified',
    unusedItems: '/unused-items',
    untaggedItems: '/untagged-items',
  },
  workspaceAdmin: {
    addOns: '/workspace/add-ons',
    checkout: '/workspace/subscription/checkout',
    customFields: '/workspace/custom-fields',
    tags: '/workspace/tags',
    cdnLinks: '/workspace/cdn-links',
    edit: '/workspace/edit',
    invoices: '/workspace/invoices',
    subscription: '/workspace/subscription',
    security: '/workspace/security',
    people: '/workspace/people',
    adInsightsAccounts: '/workspace/ad-insights-accounts',
    imports: '/workspace/imports',
  },
  workspaceSettings: {
    root: '/workspace',
    edit: '/workspace/edit',
    members: '/workspace/users',
  },
  people: '/people',
  selectSSOProviderToLoginTo: '/select-sso-provider-to-login-to',
  portal: {
    development: 'https://qa.portal.air.inc',
    preview: 'https://qa.portal.air.inc',
    production: 'https://portal.air.inc',
  },
  creditsHelp: 'https://help.air.inc/en/articles/14178814-explore-credits',
};

/** Routes where the canvas split-panel cannot be shown. Adding a route here affects both
 *  programmatic opens (useSelectedCanvasId) and URL-driven hydration (useSelectedCanvasIdSync). */
export const CANVAS_EXCLUDED_ROUTES = new Set([Routes.creativePerformance, Routes.media.recentlyDeleted]);

export const workspaceRoutes = [Object.values(Routes.workspaceSettings), Object.values(Routes.workspaceAdmin)].flat();
export const accountSettingsRoutes = Object.values(Routes.account);

export const createAllContentAssetRoute = (id: string) => `${Routes.media.all}/c/${id}`;
export const createShareLinkCollectionRoute = (collectionId: string) =>
  `${Routes.shareLinks}/collection/${collectionId}`;

export const getJoinWorkspaceURL = (shortId: string, email: string) =>
  Routes.joinWorkspace.replace('[shortId]', shortId).concat(`?${stringify({ email })}`);

export const getDesktopAppLink = (pathname: string, search = '') => {
  return `airapp://.${pathname}/${search}`;
  //               ^           ^
  // Both the dot and trailing slash in this url are significant. The
  // desktop app will receive this url and extract pathname using
  // `new URL('airapp://./...')` and the dot ensures that the url is valid
  // since it the pathname will include a backslash at the beginning.
};

export const AirFlowPageLink = 'https://air.inc/air-flow';
